import React, { useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { OptionsCumulativePnlChart, OptionsDailyPnlChart } from "./OptionsPnlCharts";
import useOptionsPnlAnalysis from "../options/useOptionsPnlAnalysis";
import "../FundingAccountPage/fundingAccount.css";
import "../SpotOrders/spotOrders.css";
import "./optionsPnlAnalysis.css";

function num(v) {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function fmt(val, decimals = 4) {
  const n = num(val);
  return parseFloat(n.toFixed(decimals)).toString();
}

function fmtSigned(val, decimals = 4) {
  const n = num(val);
  const abs = fmt(Math.abs(n), decimals);
  if (n > 0) return `+${abs}`;
  if (n < 0) return `-${abs}`;
  return abs;
}

function pnlClass(val) {
  const n = num(val);
  if (n > 0) return "options-pnl-pos";
  if (n < 0) return "options-pnl-neg";
  return "options-pnl-neutral";
}

function fmtUpdatedAt(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function DatePickerCell({ value, onChange, ariaLabel, inputRef }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">
        {value || "—"}
      </span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

function SummaryCard({ label, card, updatedAt, showValues }) {
  const pnl = num(card?.pnl_usdt);
  const pct = num(card?.pnl_pct);
  return (
    <div className="funding-account__summarycard options-pnl-metric-card">
      <div className="options-pnl-card-k">{label}</div>
      <div className={`options-pnl-card-v ${pnlClass(pnl)}`}>
        {showValues ? `${fmtSigned(pct, 2)}%` : "****"}
      </div>
      <div className={`options-pnl-card-sub ${pnlClass(pnl)}`}>
        {showValues ? `${fmtSigned(pnl, 4)} USDT` : "****"}
      </div>
      <div className="options-pnl-card-foot">
        {label === "Daily PNL" && updatedAt ? (
          <div className="options-pnl-updated">Update Time: {fmtUpdatedAt(updatedAt)}</div>
        ) : null}
      </div>
    </div>
  );
}

export default function OptionsPnlAnalysisPage() {
  const navigate = useNavigate();
  const [showBalance, setShowBalance] = useState(true);
  const [viewTab, setViewTab] = useState("overview");
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const dateRowRef = useRef(null);
  
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const handleScroll = () => {
    if (dateRowRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = dateRowRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  React.useEffect(() => {
    handleScroll();
    window.addEventListener("resize", handleScroll);
    return () => window.removeEventListener("resize", handleScroll);
  }, []);

  const token = useMemo(() => {
    try {
      return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    } catch {
      return "";
    }
  }, []);

  const {
    period,
    dateFrom,
    dateTo,
    analysis,
    detailsRows,
    detailsPagination,
    detailsPage,
    loading,
    error,
    applyPeriod,
    setCustomRange,
    setDetailsPage,
  } = useOptionsPnlAnalysis({ token, enabled: Boolean(token) });

  const summary = analysis?.range_summary;
  const cards = analysis?.summary_cards;
  const chart = analysis?.chart;
  const detailsPages = detailsPagination?.total_pages ?? 1;

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.focus();
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="dashboard_right funding-account margin-pro-page options-account-page options-pnl-page">
      <button type="button" className="options-pnl-back" onClick={() => navigate("/user_profile/wallet/options")}>
        <i className="ri-arrow-left-s-line" /> Previous
      </button>

      <div className="funding-account__header">
        <h1 className="funding-account__title options-pnl-page-title mb-0">
          Options Profit &amp; Loss Analysis
          <button
            type="button"
            className="options-pnl-visibility-btn"
            onClick={() => setShowBalance((v) => !v)}
            aria-label={showBalance ? "Hide balances" : "Show balances"}
          >
            <i className={showBalance ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
          </button>
        </h1>
      </div>

      {error && (
        <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
          {error}
        </div>
      )}

      <div className="options-pnl-cards">
        <SummaryCard label="Daily PNL" card={cards?.daily} updatedAt={analysis?.updated_at} showValues={showBalance} />
        <SummaryCard label="7D PNL" card={cards?.["7d"]} showValues={showBalance} />
        <SummaryCard label="30D PNL" card={cards?.["30d"]} showValues={showBalance} />
      </div>

      <div className="funding-account__summarycard options-pnl-main-panel">
        <div className="options-pnl-toolbar">
          <div className="cmp-pnl-btn-group">
            {["7d", "30d", "90d"].map((p) => (
              <button
                key={p}
                type="button"
                className={`cmp-pnl-btn ${period === p ? "active" : ""}`}
                onClick={() => applyPeriod(p)}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="options-pnl-date-filter" style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            {showLeftArrow && (
              <button className="dock_scroll_btn left" onClick={() => dateRowRef.current?.scrollBy({ left: -150, behavior: 'smooth' })}>
                &#10094;
              </button>
            )}
            <div className="so-dateRow" ref={dateRowRef} onScroll={handleScroll}>
              <DatePickerCell
                value={dateFrom}
                onChange={(v) => { setCustomRange(v, null); setTimeout(handleScroll, 100); }}
                ariaLabel="Start date"
                inputRef={fromRef}
              />
              <span className="so-dateArrow" aria-hidden="true">→</span>
              <DatePickerCell
                value={dateTo}
                onChange={(v) => { setCustomRange(null, v); setTimeout(handleScroll, 100); }}
                ariaLabel="End date"
                inputRef={toRef}
              />
              <button
                type="button"
                className="so-dateCalBtn"
                aria-label="Open calendar"
                onClick={() => openDatePicker(fromRef)}
              >
                <i className="ri-calendar-line" />
              </button>
            </div>
            {showRightArrow && (
              <button className="dock_scroll_btn right" onClick={() => dateRowRef.current?.scrollBy({ left: 150, behavior: 'smooth' })}>
                &#10095;
              </button>
            )}
          </div>
        </div>

        <div className="options-pnl-section-title">Profit and Loss Summary</div>
        {loading ? (
          <div className="cmp-loading-text py-3">Loading…</div>
        ) : (
          <div className="options-pnl-summary-grid">
            <div>
              {[
                ["Total Profit", summary?.total_profit],
                ["Total Loss", summary?.total_loss],
                ["Net PNL", summary?.net_pnl],
              ].map(([label, value]) => (
                <div className="options-pnl-summary-row" key={label}>
                  <span>{label}</span>
                  <span className={showBalance ? pnlClass(value) : ""}>
                    {showBalance ? `${fmtSigned(value, 4)} USDT` : "****"}
                  </span>
                </div>
              ))}
            </div>
            <div>
              {[
                ["Win Rate", summary?.win_rate_pct, true],
                ["Winning Days", summary?.winning_days, false],
                ["Losing Days", summary?.losing_days, false],
                ["Breakeven Days", summary?.breakeven_days, false],
              ].map(([label, value, isPct]) => (
                <div className="options-pnl-summary-row" key={label}>
                  <span>{label}</span>
                  <span>
                    {showBalance
                      ? (isPct ? `${fmt(value, 2)}%` : String(value ?? 0))
                      : "****"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="cmp-tabs-header mt-3">
          <div className="cmp-tabs-group">
            {["overview", "details"].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`cmp-tab-btn ${viewTab === tab ? "active" : ""}`}
                onClick={() => setViewTab(tab)}
              >
                {tab === "overview" ? "Overview" : "Details"}
              </button>
            ))}
          </div>
        </div>

        {viewTab === "overview" && !loading && (
          <div className="row g-3 options-pnl-charts-row">
            <div className="col-lg-6">
              <OptionsDailyPnlChart data={chart?.daily_account_pnl} />
            </div>
            <div className="col-lg-6">
              <OptionsCumulativePnlChart
                usdtData={chart?.cumulative_pnl_usdt}
                pctData={chart?.cumulative_pnl_pct}
              />
            </div>
          </div>
        )}

        {viewTab === "details" && (
          <div className="funding-account__tablewrap mt-3">
            <table className="funding-account__table funding-account__table--futures">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="ta-r">Total Equity (USDT)</th>
                  <th className="ta-r">Daily PNL (USDT)</th>
                  <th className="ta-r">Cumulative PNL (USDT/%)</th>
                  <th className="ta-r">Net Transfer (USDT)</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-4 text-muted">Loading…</td></tr>
                ) : detailsRows.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-4 text-muted">No data</td></tr>
                ) : detailsRows.map((row) => {
                  const daily = num(row.daily_pnl);
                  const cum = num(row.cumulative_pnl);
                  const cumPct = num(row.cumulative_pnl_pct);
                  return (
                    <tr key={row.date}>
                      <td>{row.date}</td>
                      <td className="ta-r">{showBalance ? fmt(row.total_equity, 4) : "****"}</td>
                      <td className={`ta-r ${showBalance ? pnlClass(daily) : ""}`}>
                        {showBalance ? fmtSigned(daily, 4) : "****"}
                      </td>
                      <td className={`ta-r ${showBalance ? pnlClass(cum) : ""}`}>
                        {showBalance ? `${fmtSigned(cum, 4)} (${fmtSigned(cumPct, 2)}%)` : "****"}
                      </td>
                      <td className="ta-r">{showBalance ? fmtSigned(row.net_transfer, 4) : "****"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {viewTab === "details" && detailsPages > 1 && (
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button
              type="button"
              className="funding-account__pill"
              disabled={detailsPage <= 1}
              onClick={() => setDetailsPage((p) => Math.max(1, p - 1))}
            >
              ‹
            </button>
            <span className="align-self-center text-muted small">Page {detailsPage} / {detailsPages}</span>
            <button
              type="button"
              className="funding-account__pill"
              disabled={detailsPage >= detailsPages}
              onClick={() => setDetailsPage((p) => p + 1)}
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
